import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { uploadFileToBlob } from '../api/services/storageService';
import { useTheme } from '../context/ThemeContext';
import { emailAssetService } from '../api';
import { Button } from '../components';

// --- Styled Components ---

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${p => p.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${p => p.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${p => p.theme.shadows?.xl || '0 25px 50px rgba(0,0,0,0.25)'};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 4px 10px;
  border-radius: ${p => p.theme.borderRadius.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  font-size: 12px;
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const UploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const HiddenInput = styled.input`
  display: none;
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${p => p.theme.spacing.md};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: ${p => p.theme.spacing.sm} 0;
`;

const AssetCard = styled.div`
  border: 2px solid ${p => p.$selected ? p.theme.colors.tint : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${p => p.theme.colors.backgroundSecondary};

  &:hover {
    border-color: ${p => p.theme.colors.tint};
  }
`;

const AssetThumb = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.background};
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const AssetInfo = styled.div`
  padding: 6px 8px;
`;

const AssetName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${p => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AssetSize = styled.div`
  font-size: 10px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.muted};
  font-size: 14px;
  grid-column: 1 / -1;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.sm};
  margin-top: ${p => p.theme.spacing.md};
  padding-top: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${p => p.theme.colors.border};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${p => p.theme.colors.tint};
  width: ${p => p.$progress}%;
  transition: width 0.3s ease;
`;

// --- Helpers ---

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// --- Component ---

export const EmailAssetPickerModal = ({ isOpen, onClose, onSelect }) => {
  const theme = useTheme();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await emailAssetService.getAll();
      const items = response?.data || response;
      setAssets(Array.isArray(items) ? items : []);
    } catch {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadAssets();
      setSelectedAsset(null);
    }
  }, [isOpen, loadAssets]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Only image files (PNG, JPEG, GIF, WebP, SVG) are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5 MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // 1. Upload via per-file presigned URL
      const { blobPath: tempPath } = await uploadFileToBlob(file, (loaded, total, pct) => {
        setUploadProgress(pct);
      });

      // 2. Register asset (backend copies from org temp to public bucket)
      const regResp = await emailAssetService.register({
        fileName: file.name,
        tempBlobPath: tempPath,
        contentType: file.type,
        fileSize: file.size,
      });
      const newAsset = regResp?.data || regResp;

      setAssets(prev => [newAsset, ...prev]);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSelect = () => {
    if (!selectedAsset) return;
    onSelect(selectedAsset.url, selectedAsset.altText || selectedAsset.fileName);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} theme={theme} onClick={onClose}>
      <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <Title theme={theme}>Email Asset Library</Title>
          <CloseButton theme={theme} onClick={onClose}>X</CloseButton>
        </ModalHeader>

        <UploadArea theme={theme}>
          <HiddenInput
            type="file"
            id="asset-upload"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            onChange={handleUpload}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => document.getElementById('asset-upload').click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
          </Button>
          {uploading && (
            <span style={{ fontSize: 12, color: theme.colors.muted }}>{uploadProgress}%</span>
          )}
        </UploadArea>

        {uploading && (
          <ProgressBar theme={theme}>
            <ProgressFill theme={theme} $progress={uploadProgress} />
          </ProgressBar>
        )}

        <AssetGrid theme={theme}>
          {loading ? (
            <EmptyState theme={theme}>Loading assets...</EmptyState>
          ) : assets.length === 0 ? (
            <EmptyState theme={theme}>No images uploaded yet. Upload one to get started.</EmptyState>
          ) : (
            assets.map(asset => (
              <AssetCard
                key={asset.id}
                theme={theme}
                $selected={selectedAsset?.id === asset.id}
                onClick={() => setSelectedAsset(asset)}
                onDoubleClick={() => {
                  setSelectedAsset(asset);
                  onSelect(asset.url, asset.altText || asset.fileName);
                  onClose();
                }}
              >
                <AssetThumb theme={theme}>
                  <img src={asset.url} alt={asset.altText || asset.fileName} loading="lazy" />
                </AssetThumb>
                <AssetInfo>
                  <AssetName theme={theme}>{asset.fileName}</AssetName>
                  <AssetSize theme={theme}>{formatFileSize(asset.fileSize)}</AssetSize>
                </AssetInfo>
              </AssetCard>
            ))
          )}
        </AssetGrid>

        <ModalActions theme={theme}>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSelect} disabled={!selectedAsset}>
            Select
          </Button>
        </ModalActions>
      </ModalContent>
    </Overlay>
  );
};
